/* ============================================================================
   Furniva "Buy now" — the Razorpay Magic Checkout product-page route.

   ----------------------------------------------------------------------------
   What changed, and why this file no longer calls a Furniva endpoint
   ----------------------------------------------------------------------------

   The earlier version of this file posted to a Furniva-owned endpoint that was
   supposed to mint an isolated single-product checkout. That endpoint never
   existed, because Razorpay has no such session: Magic Checkout always reads
   the Shopify cart. The button therefore never rendered.

   The Razorpay COD & Magic Checkout app is now installed and its app embed is
   enabled on this theme, so the real route is available. Read from the live
   storefront and from the app's own `magic-sopc.js`, it works like this:

     · The embed prints its settings into `#RazorpayMagicSopcConfig`, and the
       script attaches ONE capturing click listener to `document`.
     · On every click it walks a selector map. `productConfig.customSelector`
       is the product-page hook: a click on anything matching it is taken as
       "buy this one product now".
     · That handler POSTs `/cart/clear.js`, then `/cart/add.js` with the
       selected variant and quantity read out of the product form, then opens
       Magic Checkout on the resulting one-line cart.
     · An element carrying `data-magicx="inactive"` — on itself or on any
       ancestor — is skipped by the listener entirely. That is the app's own
       opt-out, and it is what lets this file get a word in first.

   So Buy now really does check out one piece on its own. What it does NOT do
   is leave the shopper's existing cart alone: Razorpay CLEARS it first. That is
   a verified fact about the live integration, not a guess, and it is the one
   thing this file exists to prevent.

   ----------------------------------------------------------------------------
   The rule
   ----------------------------------------------------------------------------

   Buy now never destroys a cart. It takes one of two routes:

     · The bag is empty — nothing to lose, so the click is handed to Razorpay
       and Magic Checkout opens on this piece alone.
     · The bag holds anything — any line, any quantity, including more of the
       very piece being bought — so Razorpay is never reached. This piece is
       ADDED to what is already there and the customer goes to /cart, where the
       checkout control is claimed by the same Razorpay listener and opens
       Magic Checkout on the combined bag.

   An unreadable bag takes the second route, because adding and routing is safe
   whether or not anything was in it.

   ----------------------------------------------------------------------------
   What this file does
   ----------------------------------------------------------------------------

   1. Renders nothing unless the click will actually be taken. The button ships
      hidden; it is shown only when the embed is present, live, and its
      configured product selector matches this button. A control drawn dead is
      worse than no control.

   2. Reads `/cart.js` on every press and only hands off when the bag is empty.
      Otherwise it adds and routes to the cart, as above.

   4. Resyncs the header count and the drawer through the theme's own cart
      renderer when Magic Checkout is closed without paying, because Razorpay
      will have changed the cart underneath a page that still shows the old one.

   3. Hands off by removing `data-magicx` for exactly one synthetic click, then
      putting it straight back. The dispatch is synchronous, so the attribute is
      absent only for the instant Razorpay's listener runs.

   No Razorpay key, secret, MID or account identifier appears here, and none
   should ever be added. Everything this file reads is already public in the
   page the embed renders.

   Owner action, once: set the Magic Checkout product-page selector to
   `[data-fv-buy-now]` in the Razorpay dashboard. Until that is set the button
   stays hidden by rule 1. See docs/CHECKOUT.md.
   ============================================================================ */
(function () {
  'use strict';

  var button = document.querySelector('[data-fv-buy-now]');
  if (!button) return;

  /* ---------------------------------------------------------------------- *
   * Will Magic Checkout take this button's click?
   * ---------------------------------------------------------------------- */

  function readJson(id) {
    var node = document.getElementById(id);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return null;
    }
  }

  /* Two Razorpay integrations exist:
       · the public app's embed (`#RazorpayMagicSopcConfig`, magic-sopc.js),
         which takes the click through a product-page selector; and
       · the custom "hijack" app installed on 2026-10-08
         (`#RazorpayMagicCheckoutConfig`, magic-shopify.js), which opens
         Magic Checkout from any standard cart checkout button. With it, Buy now
         adds the piece itself and presses a hidden checkout button, so Magic
         opens over this page without Razorpay ever clearing a cart. */
  var hijack = readJson('RazorpayMagicCheckoutConfig');
  var config = hijack ? null : readJson('RazorpayMagicSopcConfig');
  if (!hijack && !config) return; // no Magic Checkout on this page

  if (hijack) {
    var hc = hijack.config || {};
    var cartLive = (hc.cartConfig && hc.cartConfig.status) === 'live';
    if (!cartLive && window.location.search.indexOf('enable_magic=true') < 0) return;
  } else {
    // The embed's own gate: it only opens for a live account, unless a tester
    // has asked for it explicitly. Match it rather than guess at it.
    var live = config.status === 'live' || window.location.search.indexOf('enable_magic_x=1') > -1;
    if (!live) return;

    // Razorpay matches `e.target.matches(sel) || e.target.closest(sel)`, so a
    // click on the label span resolves to the button. Testing the button is the
    // same question asked once.
    var selector = (config.productConfig && config.productConfig.customSelector) || '';
    if (!selector) return; // product-page hook not configured yet
    try {
      if (!button.matches(selector)) return;
    } catch (error) {
      return; // an unparseable selector is a selector that will never match
    }
  }

  button.hidden = false;

  /* ---------------------------------------------------------------------- *
   * The control
   * ---------------------------------------------------------------------- */

  var state = document.querySelector('[data-fv-buy-now-state]');
  var label = button.querySelector('[data-fv-buy-now-label]');
  var original = label ? label.textContent : '';

  var handingOff = false; // a synthetic click is in flight

  function say(message, tone) {
    if (!state) return;
    state.textContent = message || '';
    state.className = 'fv-buy__now-state' + (tone ? ' is-' + tone : '');
  }

  function busy(on, text) {
    button.disabled = !!on;
    if (label) label.textContent = on ? (text || 'Adding…') : original;
  }

  /* Razorpay draws its own "Processing…" overlay, with a spinner, on top of
     whatever it just claimed. Our label underneath it made two words occupy one
     line of pixels. So for the hand-off we keep the label's box — the button
     must not change size — and hide its text, leaving exactly one readable
     state. `is-handing-off` does that in furniva-product.css. */
  function handoffVisual(on) {
    button.disabled = !!on;
    button.classList.toggle('is-handing-off', !!on);
    if (label) label.textContent = original;
  }

  function selectedVariant() {
    var hidden = document.querySelector('[data-fv-variant-id]');
    var id = hidden ? Number(hidden.value) : Number(button.dataset.variant);
    return id || null;
  }

  function selectedQuantity() {
    var form = document.getElementById('fv-product-form');
    var input = form && form.querySelector('input[name="quantity"]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return !qty || qty < 1 ? 1 : qty;
  }

  // Everything in the bag, whatever it is.
  function itemsInCart() {
    return window
      .fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('The cart could not be read.');
        return response.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var total = 0;
        for (var i = 0; i < items.length; i++) total += items[i].quantity || 1;
        return total;
      });
  }

  /* The bag already holds something. Razorpay's own buy-now would clear it, so
     this route never reaches Razorpay: add this piece to what is already there
     and send the customer to the cart, where the checkout control opens the
     same Magic Checkout on the combined bag. Nothing is removed. */
  function addThenGoToCart(variantId) {
    return window
      .fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: selectedQuantity() }] })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('This could not be added to your bag.');
        window.location.assign('/cart');
      });
  }

  /* Magic Checkout opens over this page. Closing it without paying leaves the
     Shopify cart holding the line Razorpay added, while the header count and
     the drawer still show what was there before. So when the overlay goes, ask
     the theme's own cart renderer to catch up. Nothing here clears anything. */
  function resyncWhenCheckoutCloses() {
    var settled = false;
    function sync() {
      if (settled) return;
      settled = true;
      clearInterval(watch);
      document.removeEventListener('visibilitychange', onVisible);
      // The button was disabled and its label hidden for the hand-off. Magic
      // Checkout has gone, so give the control back — otherwise a customer who
      // closes the overlay is left with a dead Buy now until they reload.
      handoffVisual(false);
      if (window.furnivaCartDrawer && window.furnivaCartDrawer.refresh) {
        window.furnivaCartDrawer.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }
    function overlayShowing() {
      var frame = document.querySelector('iframe[src*="razorpay"], iframe[id*="magic"]');
      return !!(frame && frame.offsetParent !== null);
    }
    function onVisible() { if (!document.hidden && seen) sync(); }
    var seen = false;
    var ticks = 0;
    var watch = setInterval(function () {
      ticks += 1;
      if (overlayShowing()) { seen = true; return; }
      if (seen) { sync(); return; }
      if (ticks > 1200) { clearInterval(watch); } // ten minutes, then stop watching
    }, 500);
    document.addEventListener('visibilitychange', onVisible);
  }

  /* Hijack app: the bag is empty, so put this piece in it and press a standard
     cart checkout button (kept off-screen). Razorpay's listener claims that
     press and opens Magic Checkout over this page; if it ever does not, the
     form simply goes to checkout with the same one-line bag. */
  function addThenOpenMagic(variantId) {
    return window
      .fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: selectedQuantity() }] })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('This could not be added to your bag.');
        var form = document.getElementById('fv-buy-now-checkout');
        if (!form) {
          form = document.createElement('form');
          form.id = 'fv-buy-now-checkout';
          form.action = '/cart';
          form.method = 'post';
          form.setAttribute('aria-hidden', 'true');
          form.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
          form.innerHTML = '<button type="submit" name="checkout" tabindex="-1">Checkout</button>';
          document.body.appendChild(form);
        }
        handoffVisual(true);
        form.querySelector('button[name="checkout"]').click();
        resyncWhenCheckoutCloses();
      });
  }

  // One synthetic click with the opt-out lifted, then the opt-out back on.
  // `defaultPrevented` is the receipt: Razorpay's handler calls preventDefault,
  // so if it comes back false the click was not taken and nothing has happened.
  function handOff() {
    handingOff = true;
    button.removeAttribute('data-magicx');
    handoffVisual(true);

    var handoff;
    try {
      handoff = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
      button.dispatchEvent(handoff);
    } finally {
      button.setAttribute('data-magicx', 'inactive');
      handingOff = false;
    }

    if (handoff && handoff.defaultPrevented) {
      say('');
      resyncWhenCheckoutCloses();
      return;
    }

    // Nothing took the click. The cart is untouched, so there is nothing to
    // undo — only to say so, and leave Add to cart working.
    handoffVisual(false);
    busy(false);
    say('Checkout could not be opened. Add to cart still works.', 'error');
  }

  button.addEventListener('click', function (event) {
    if (handingOff) return; // our own click, on its way past us to Razorpay
    event.preventDefault();

    var variantId = selectedVariant();
    if (!variantId) return;

    busy(true);
    say('');

    itemsInCart()
      .then(function (count) {
        if (count) return addThenGoToCart(variantId);
        busy(false);
        if (hijack) return addThenOpenMagic(variantId);
        handOff();
      })
      .catch(function () {
        // Either the bag could not be read or the add failed. Adding and going
        // to the cart is safe whichever it was, so try that once; if even that
        // fails, say so and leave Add to cart working.
        return addThenGoToCart(variantId).catch(function () {
          busy(false);
          say('We could not open checkout. Add to cart still works.', 'error');
        });
      });
  });

  // Keep the control honest as the finish changes: a sold-out variant gets no
  // Buy now, exactly as it gets no Add to cart. A different variant is a
  // different purchase, so anything said about the last one is cleared.
  document.addEventListener('furniva:variant:change', function (event) {
    var detail = event && event.detail;
    if (!detail || !detail.id) return;
    button.dataset.variant = detail.id;
    button.hidden = detail.available === false;
    busy(false);
    say('');
  });
})();
